import React, { useState, useEffect } from 'react';
import { Lock, Unlock, Shield, Info } from 'lucide-react';
import { getLockedFields, setLockedFields } from '../../utils/collegeAIUpdate';

const FIELD_CATEGORIES = {
  'Student Strategy': [
    'student_advantages',
    'application_strategy',
    'strategic_classification',
    'recommended_strategy'
  ],
  'Custom Content': [
    'notes',
    'custom_notes',
    'parent_notes',
    'counselor_notes'
  ],
  'Basic Information': [
    'name',
    'location',
    'city',
    'state',
    'type',
    'control'
  ],
  'Admissions Data': [
    'acceptance_rate',
    'ed_acceptance_rate',
    'ea_acceptance_rate',
    'sat_range',
    'act_range',
    'gpa_range'
  ],
  'Financial Information': [
    'tuition',
    'tuition_in_state',
    'tuition_out_of_state',
    'room_board',
    'total_cost',
    'average_aid'
  ],
  'Academic Programs': [
    'popular_majors',
    'special_programs',
    'study_abroad_rate',
    'student_faculty_ratio'
  ],
  'Institutional Values': [
    'core_values',
    'student_qualities',
    'institutional_priorities',
    'campus_culture'
  ],
  'Rankings': [
    'usnwr_ranking',
    'engineering_ranking',
    'nursing_ranking',
    'business_ranking'
  ]
};

const FieldLockManager = ({ collegeId, collegeName, onClose, isModal = true }) => {
  const [lockedFields, setLocalLockedFields] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set(['Student Strategy', 'Custom Content']));

  useEffect(() => {
    // Load current locked fields
    const currentLocked = getLockedFields(collegeId);
    setLocalLockedFields(currentLocked);
  }, [collegeId]);

  const handleToggleField = (fieldName) => {
    const newLocked = lockedFields.includes(fieldName)
      ? lockedFields.filter(f => f !== fieldName)
      : [...lockedFields, fieldName];
    
    setLocalLockedFields(newLocked);
    setLockedFields(collegeId, newLocked);
  };

  const handleToggleCategory = (category) => {
    const fields = FIELD_CATEGORIES[category];
    const allLocked = fields.every(f => lockedFields.includes(f));
    
    let newLocked;
    if (allLocked) {
      // Unlock all in category
      newLocked = lockedFields.filter(f => !fields.includes(f));
    } else {
      // Lock all in category
      newLocked = [...new Set([...lockedFields, ...fields])];
    }
    
    setLocalLockedFields(newLocked);
    setLockedFields(collegeId, newLocked);
  };

  const toggleCategoryExpansion = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const content = (
    <div style={{ padding: isModal ? '24px' : '0' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#0f172a',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Shield size={28} />
          Protected Fields
        </h2>
        <p style={{ color: '#57534e', fontSize: '14px', lineHeight: 1.6 }}>
          Locked fields will not be overwritten when using "Auto Update with AI".
          This protects your custom data and strategic insights.
        </p>
      </div>

      <div style={{
        background: '#fef3c7',
        border: '2px solid #f59e0b',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '24px',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start'
      }}>
        <Info size={20} style={{ color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '13px', color: '#92400e' }}>
          <strong>Recommended:</strong> Keep "Student Strategy" and "Custom Content" locked to preserve your personalized college insights and notes.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {Object.entries(FIELD_CATEGORIES).map(([category, fields]) => {
          const allLocked = fields.every(f => lockedFields.includes(f));
          const someLocked = fields.some(f => lockedFields.includes(f));
          const isExpanded = expandedCategories.has(category);
          
          return (
            <div key={category} style={{
              border: '2px solid #e7e5e4',
              borderRadius: '8px',
              overflow: 'hidden',
              background: allLocked ? '#fef3c7' : 'white'
            }}>
              <div style={{
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                borderBottom: isExpanded ? '2px solid #e7e5e4' : 'none'
              }}
              onClick={() => toggleCategoryExpansion(category)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#0f172a'
                  }}>
                    {category}
                  </span>
                  {allLocked && (
                    <span style={{
                      background: '#f59e0b',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Lock size={12} />
                      ALL LOCKED
                    </span>
                  )}
                  {!allLocked && someLocked && (
                    <span style={{
                      background: '#e7e5e4',
                      color: '#57534e',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>
                      PARTIAL
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleCategory(category);
                    }}
                    style={{
                      padding: '6px 12px',
                      background: allLocked ? '#dc2626' : '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {allLocked ? (
                      <>
                        <Unlock size={14} />
                        Unlock All
                      </>
                    ) : (
                      <>
                        <Lock size={14} />
                        Lock All
                      </>
                    )}
                  </button>
                  <span style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    color: '#57534e'
                  }}>
                    ▼
                  </span>
                </div>
              </div>
              
              {isExpanded && (
                <div style={{ padding: '12px 16px', background: '#fafaf9' }}>
                  {fields.map(field => {
                    const isLocked = lockedFields.includes(field);
                    const displayName = field.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ');
                    
                    return (
                      <label key={field} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        marginBottom: '4px',
                        background: isLocked ? '#fef3c7' : 'white',
                        border: `2px solid ${isLocked ? '#f59e0b' : '#e7e5e4'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}>
                        <span style={{
                          fontSize: '14px',
                          color: '#0f172a',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {isLocked && <Lock size={14} style={{ color: '#f59e0b' }} />}
                          {displayName}
                        </span>
                        <input
                          type="checkbox"
                          checked={isLocked}
                          onChange={() => handleToggleField(field)}
                          style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer'
                          }}
                        />
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: '#f0f9ff',
        border: '2px solid #0284c7',
        borderRadius: '8px'
      }}>
        <div style={{ fontSize: '14px', color: '#075985' }}>
          <strong>{lockedFields.length} fields protected</strong>
          {lockedFields.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '13px' }}>
              These fields will be preserved during AI updates: {' '}
              {lockedFields.slice(0, 3).join(', ')}
              {lockedFields.length > 3 && `, and ${lockedFields.length - 3} more`}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (!isModal) {
    return content;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {content}
        
        <div style={{
          padding: '16px 24px',
          borderTop: '2px solid #e7e5e4',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          background: 'white',
          position: 'sticky',
          bottom: 0
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: '#0f172a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default FieldLockManager;